import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { ToasterService } from '../../../../services/common/toaster.service';
import { CommonService } from '../../../../services/common';
import { SalesService } from '../../sales.service';
import { DocumentsService } from '../../../../services/common/documents.service';
import { environment } from '../../../../../environments/environment';
import { IconList } from '../../../../../iconlist';
import { ErrorMessages } from '../../../../../errormessages';
import { DocumentSubType } from '../../../../../appconfig';

@Component({
    selector: 'app-booking-cancellation-modal',
    templateUrl: './booking-cancellation-modal.component.html',
    styleUrl: './booking-cancellation-modal.component.scss'
})
export class BookingCancellationModalComponent implements OnInit, OnChanges {
    @Input() isVisible: boolean = false;
    @Input() mode: 'create' | 'edit' = 'create'; // 'create' for new cancellation, 'edit' for existing
    @Input() bookingData: any;

    @Output() closeModal = new EventEmitter<void>();
    @Output() successfulSubmit = new EventEmitter<any>(); // Emit event when API call succeeds

    // Local variables
    cancellationData: any; // Stores the API response for cancellation details (set during API fetch)
    cancellationReason: string = '';
    cancellationDate: Date = new Date();
    maxDate: Date = new Date();
    cancellationScreenshot: any;
    uploadedCancellationFile: any;
    displayDocumentFileDialogue: boolean = false;
    documentFileData: any;
    showclickedPic: boolean = false;
    picViewData: any;
    cancellationScreenshotIcon: any = environment.assetsImagePath + IconList.AddIcon;
    isSubmitting: boolean = false; // Track submission state

    // Document tracking
    hasDocumentChanged: boolean = false; // Track if document was changed by user in edit mode

    constructor(
        private toasterService: ToasterService,
        public commonService: CommonService,
        private salesService: SalesService,
        private documentsService: DocumentsService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        // Initial setup
    }

    /**
     * Detect changes in input properties and update form accordingly
     */
    ngOnChanges(changes: SimpleChanges): void {
        // Initialize form when modal becomes visible
        if (changes['isVisible']?.currentValue === true) {
            // If in edit mode, fetch cancelled details from API first
            if (this.mode === 'edit' && this.bookingData?.id) {
                this.fetchCancellationDetailsForEdit();
            } else {
                // For create mode, just initialize form
                this.initializeForm();
            }
        }
    }

    /**
     * Fetch cancellation details from API in edit mode
     */
    private fetchCancellationDetailsForEdit(): void {
        this.salesService.GetBookingCancellationDetails(this.bookingData.id).subscribe({
            next: (res) => {
                if (res && res.result) {
                    // Update cancellation data with API response (single object, not array)
                    this.cancellationData = res.result;

                    // Initialize form with fetched data
                    this.initializeForm();

                    // Fetch document if documentId exists in response
                    this.fetchExistingDocument();
                } else {
                    this.toasterService.showError('Failed to fetch cancellation details.');
                    this.isVisible = false;
                }
            },
            error: (err) => {
                this.toasterService.showError(err?.error?.message || 'Failed to fetch cancellation details.');
                this.isVisible = false;
            }
        });
    }

    /**
     * Initialize form based on mode (create/edit)
     */
    initializeForm(): void {
        if (this.mode === 'create') {
            this.resetForm();
            this.hasDocumentChanged = false;
        } else if (this.mode === 'edit' && this.cancellationData) {
            // Populate form with existing cancellation data from API response
            this.cancellationReason = this.cancellationData?.requestRemark || '';

            // Convert GMT datetime from API to user's local timezone
            if (this.cancellationData?.requestedOn) {
                this.cancellationDate = this.commonService.convertToLocalTimeZone(this.cancellationData.requestedOn);
            } else {
                this.cancellationDate = new Date();
                this.cancellationDate.setHours(0, 0, 0, 0);
            }

            this.cancellationScreenshotIcon = environment.assetsImagePath + IconList.EditIcon;
            this.hasDocumentChanged = false; // Reset flag: document not changed yet
        }
    }

    /**
     * Convert local date to GMT/UTC date for API submission
     * Ensures the date sent to API represents midnight GMT for the selected calendar date
     * @param localDate Local Date object
     * @returns ISO date string in GMT format
     */
    private convertLocalDateToGMT(localDate: Date): string {
        // Extract local date components
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, '0');
        const day = String(localDate.getDate()).padStart(2, '0');

        // Return ISO date string at midnight GMT (representing the entire calendar day)
        return `${year}-${month}-${day}T00:00:00Z`;
    }

    /**
     * Reset form to initial state
     */
    resetForm(): void {
        this.cancellationReason = '';
        this.cancellationDate = new Date();
        this.cancellationDate.setHours(0, 0, 0, 0);
        this.cancellationScreenshot = null;
        this.uploadedCancellationFile = null;
        this.cancellationScreenshotIcon = environment.assetsImagePath + IconList.AddIcon;
        this.hasDocumentChanged = false;
    }

    /**
     * Fetch existing document for this cancellation request
     * Optimized: If API response includes documentId, directly fetch document bytes
     * Otherwise, skip - document will be fetched only if available
     */
    private fetchExistingDocument(): void {
        const documentId = this.cancellationData?.documentId;

        // If documentId is included in the cancellation response, use it directly
        if (documentId) {
            this.documentsService.getDocumentBytesById(documentId).subscribe({
                next: (bytesResponse: any) => {
                    try {
                        // Convert the response to base64 string
                        let base64String = bytesResponse;

                        // If response is a Blob, convert to base64
                        if (bytesResponse instanceof Blob) {
                            const reader = new FileReader();
                            reader.onload = () => {
                                const result = reader.result as string;
                                // Extract base64 part if it has data:image/... prefix
                                base64String = result.includes(',') ? result.split(',')[1] : result;
                                // Create minimal document object with available data
                                const existingDoc = {
                                    documentId: documentId,
                                    ...this.cancellationData
                                };
                                this.loadDocumentToModal(base64String, existingDoc);
                            };
                            reader.readAsDataURL(bytesResponse);
                        } else {
                            // If already a string, use directly
                            const existingDoc = {
                                documentId: documentId,
                                ...this.cancellationData
                            };
                            this.loadDocumentToModal(base64String, existingDoc);
                        }
                    } catch (error) {
                        // Silent fail - document loading error
                    }
                },
                error: (err: any) => {
                    // Silent fail - document not found or download failed
                }
            });
        } else {
            // No documentId in response - no document exists for this cancellation request
            // Modal will display without a document
        }
    }

    /**
     * Load document data to modal
     */
    private loadDocumentToModal(base64String: string, existingDoc: any): void {
        // Defer state changes to avoid change detection issues
        setTimeout(() => {
            this.cancellationScreenshot = base64String;
            this.uploadedCancellationFile = existingDoc;
            this.cancellationScreenshotIcon = environment.assetsImagePath + IconList.EditIcon;
            this.cdr.detectChanges();
        }, 0);
    }

    /**
     * Handle form submission - make API calls directly
     */
    onSubmit(): void {
        if (!this.cancellationReason || this.cancellationReason.trim().length === 0) {
            this.toasterService.showError('Please enter cancellation reason');
            return;
        }

        this.isSubmitting = true;

        let documentDetails = null;

        // Include document only if it was changed (newly uploaded) in this session
        // hasDocumentChanged flag is set to true only when user uploads a new file
        if (this.hasDocumentChanged && this.uploadedCancellationFile) {
            documentDetails = {
                isActive: true,
                isVerified: false,
                docType: this.uploadedCancellationFile.document.name.split('.').pop(),
                fileBase64: this.cancellationScreenshot,
                documentSubTypeId: DocumentSubType['BookingCancellationRequest'],
                bookingId: this.bookingData?.id,
                projectId: this.bookingData?.projectDetail?.id
            };
        }

        if (this.mode === 'create') {
            // Create new cancellation request
            const cancellationData = {
                bookingId: this.bookingData?.id,
                requestRemark: this.cancellationReason,
                requestedOn: this.convertLocalDateToGMT(this.cancellationDate),
                documentDetails: documentDetails,
                hasDocumentChanged: this.hasDocumentChanged // true only if document was uploaded
            };

            this.salesService.MarkBookingForCancellation(cancellationData).subscribe({
                next: (result: any) => {
                    this.toasterService.showSuccess(ErrorMessages.bookingCancellationRequest);
                    this.isSubmitting = false;
                    this.successfulSubmit.emit({ mode: 'create', data: result });
                    // Close dialog
                    this.isVisible = false;
                    this.handleCloseModal();
                },
                error: (error: any) => {
                    this.toasterService.showError(error?.error?.message ?? ErrorMessages.GeneralError);
                    this.isSubmitting = false;
                }
            });
        } else if (this.mode === 'edit') {
            // Update existing cancellation request
            // Send flag indicating whether document was changed by user
            const updateData = {
                requestRemark: this.cancellationReason,
                requestedOn: this.convertLocalDateToGMT(this.cancellationDate),
                documentDetails: documentDetails,
                hasDocumentChanged: this.hasDocumentChanged // Backend uses this to decide whether to update document
            };

            this.salesService.UpdateBookingCancellationRequest(this.bookingData?.id, updateData).subscribe({
                next: (result: any) => {
                    this.toasterService.showSuccess('Cancellation request updated successfully.');
                    this.isSubmitting = false;
                    this.successfulSubmit.emit({ mode: 'edit', data: result });
                    // Close dialog
                    this.isVisible = false;
                    this.handleCloseModal();
                },
                error: (error: any) => {
                    this.toasterService.showError(error?.error?.message ?? ErrorMessages.GeneralError);
                    this.isSubmitting = false;
                }
            });
        }
    }

    /**
     * Handle dialog hide event (header close button, backdrop click, escape key)
     */
    onDialogHide(): void {
        this.handleCloseModal();
    }

    /**
     * Handle close button click
     */
    onModalClose(): void {
        this.isVisible = false; // Close dialog
        this.handleCloseModal();
    }

    /**
     * Common close handler that resets form and emits close event
     */
    private handleCloseModal(): void {
        this.resetForm();
        this.closeModal.emit();
    }

    /**
     * Sets config of current file to be uploaded for booking cancellation request
     * Passes existing document to the file upload modal so it displays the current document
     */
    setCurrentFileConfig(): void {
        const documentSubType = 'BookingCancellationRequest';
        let fileType = null;
        let documentByte = null;

        // Prepare document data if exists
        if (this.uploadedCancellationFile?.documentByte) {
            documentByte = this.uploadedCancellationFile.documentByte;
            // Determine file type from either the document name or by analyzing the base64
            if (this.uploadedCancellationFile?.document?.name) {
                fileType = this.uploadedCancellationFile.document.name.split('.').pop()?.toLowerCase();
            } else {
                // Fallback: analyze the base64 string to determine type
                fileType = this.commonService.getFileTypeFromBase64String(this.uploadedCancellationFile.documentByte);
            }
        }

        // Create NEW object reference so @Input change detection triggers in child component
        this.documentFileData = {
            'picFor': this.commonService.addSpaceInCamelCase(documentSubType),
            'documentSubTypeId': DocumentSubType[documentSubType],
            'operationMode': 'add',
            'showDocumentNumber': false,
            'documentNoHide': false,
            'documentByte': documentByte,
            'fileType': fileType
        };

        this.displayDocumentFileDialogue = true;

        // Trigger change detection to update child component with document data
        this.cdr.detectChanges();
    }

    /**
     * Receives the uploaded screenshot for booking cancellation request
     * Only updates if a new file was selected (receivedPicData with documentFile)
     * If modal closes without selection, the component returns the current data from fileDataConfig
     */
    onDocumentFileReceived(receivedPicData: any): void {
        if (receivedPicData && receivedPicData?.documentFile) {
            // New file was selected - update the document
            this.cancellationScreenshot = receivedPicData?.documentFile?.documentByte;
            this.uploadedCancellationFile = receivedPicData?.documentFile;
            this.cancellationScreenshotIcon = environment.assetsImagePath + IconList.EditIcon;
            this.hasDocumentChanged = true; // Mark that document was changed by user
        }
        // If receivedPicData is empty/null, it means user closed without selecting
        // In that case, preserve the existing document (no action needed)
        this.displayDocumentFileDialogue = false;
    }

    /**
     * Opens image preview viewer for cancellation screenshot
     */
    openImagePreview(): void {
        if (this.cancellationScreenshot) {
            const base64String = this.cancellationScreenshot.includes(',')
                ? this.cancellationScreenshot.split(',')[1]
                : this.cancellationScreenshot;

            this.picViewData = {
                'picTitle': 'Cancellation Request Screenshot',
                'picFile': base64String,
                'fileType': this.commonService.getFileTypeFromBase64String(this.cancellationScreenshot)
            };
            this.showclickedPic = true;
        }
    }

    /**
     * Get modal title based on mode
     */
    getModalTitle(): string {
        return this.mode === 'create'
            ? `Cancellation Request Details (#${this.bookingData?.id})`
            : `Edit Cancellation Request (#${this.bookingData?.id})`;
    }

    /**
     * Get submit button label based on mode
     */
    getSubmitButtonLabel(): string {
        return this.mode === 'create' ? 'Request Cancellation' : 'Update Request';
    }
}
